<script setup lang="ts">
import { useMonitoringStore } from '@/stores/monitoring.store'
import { computed, ref } from 'vue'
import type { LinkStatus } from '@dronecast/shared'
import DiagnosticPanel from '@/components/DiagnosticPanel.vue'

const store = useMonitoringStore()
const s = computed(() => store.casterState)
const showDetail = ref(false)

function statusColor(status: LinkStatus): string {
  switch (status) {
    case 'connected': return '#28a745'
    case 'unstable': return '#ffc107'
    case 'disconnected': return '#dc3545'
    default: return '#6c757d'
  }
}

function statusLabel(status: LinkStatus): string {
  switch (status) {
    case 'connected': return '원활'
    case 'unstable': return '불안정'
    case 'disconnected': return '끊김'
    default: return '미확인'
  }
}

function linkLineClass(status: LinkStatus): string {
  switch (status) {
    case 'connected': return 'link-line connected'
    case 'unstable': return 'link-line unstable'
    case 'disconnected': return 'link-line disconnected'
    default: return 'link-line unknown'
  }
}

</script>

<template>
  <div class="caster-status-wrapper">
    <!-- 상단 바 -->
    <div class="status-bar" @click="showDetail = !showDetail">
      <!-- 네트워크 -->
      <span class="network-badge" :class="{ offline: !s.network.online }">
        📡 {{ s.network.online ? '온라인' : '오프라인' }}
      </span>

      <!-- Node-Link 다이어그램 -->
      <div class="node-link">
        <span class="node">Caster</span>
        <span :class="linkLineClass(s.serverLink.state.status)">
          <span class="link-dot" :style="{ backgroundColor: statusColor(s.serverLink.state.status) }"></span>
          <span class="link-label">{{ statusLabel(s.serverLink.state.status) }}</span>
        </span>
        <span class="node">Server</span>
      </div>

      <!-- ON AIR + Player 수 -->
      <div v-if="s.casting.isOnAir" class="onair-badge">
        🔴 ON AIR &nbsp; 👤 {{ s.casting.playerCount }}
      </div>

      <span class="toggle-hint">{{ showDetail ? '▲' : '▼' }}</span>
    </div>

    <!-- 원인 메시지 -->
    <div v-if="s.serverLink.state.cause" class="cause-message" :class="s.serverLink.state.status">
      {{ s.serverLink.state.status === 'unstable' ? '⚠' : '✕' }}
      {{ s.serverLink.state.causeMessage }}
    </div>

    <!-- Player 목록 -->
    <div v-if="s.players.length > 0" class="player-list">
      <div v-for="p in s.players" :key="p.clientId" class="player-item">
        <span class="player-dot" :style="{ backgroundColor: statusColor(p.status) }"></span>
        <span class="player-id">{{ p.clientId }}</span>
        <span class="player-status">{{ statusLabel(p.status) }}</span>
        <span v-if="p.causeMessage" class="player-cause">{{ p.causeMessage }}</span>
      </div>
    </div>

    <!-- 상세 패널 (Caster ↔ Server) -->
    <div v-if="showDetail" class="detail-panel">
      <div class="detail-title">연결 상세</div>
      <DiagnosticPanel role="caster" :detail="s.serverLink.detail" />
    </div>
  </div>
</template>

<style scoped>
.caster-status-wrapper {
  margin-bottom: 8px;
  font-size: 12px;
}

.status-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
}

.network-badge {
  font-size: 11px;
  color: #495057;
  white-space: nowrap;
}
.network-badge.offline {
  color: #dc3545;
  font-weight: 600;
}

.node-link {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
}

.node {
  padding: 3px 10px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.link-line {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  position: relative;
}
.link-line::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 0;
}
.link-line.connected::before { background: #28a745; }
.link-line.unstable::before { background: #ffc107; border-top: 2px dashed #ffc107; height: 0; }
.link-line.disconnected::before { background: none; border-top: 2px dashed #dc3545; height: 0; }
.link-line.unknown::before { background: none; border-top: 2px dashed #6c757d; height: 0; }

.link-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  z-index: 1;
  flex-shrink: 0;
}
.link-label {
  font-size: 10px;
  color: #666;
  z-index: 1;
  white-space: nowrap;
}

.onair-badge {
  background: #000;
  color: #fff;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.5px;
}

.toggle-hint {
  font-size: 10px;
  color: #aaa;
}

.cause-message {
  padding: 4px 12px;
  font-size: 11px;
  margin-top: 2px;
}
.cause-message.unstable { color: #856404; }
.cause-message.disconnected { color: #dc3545; }

.player-list {
  margin-top: 4px;
  padding: 4px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}
.player-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}
.player-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.player-id {
  font-size: 11px;
  color: #495057;
  min-width: 80px;
}
.player-status {
  font-size: 10px;
  color: #666;
}
.player-cause {
  font-size: 10px;
  color: #999;
  margin-left: auto;
}

.detail-panel {
  margin-top: 4px;
}
.detail-title {
  font-size: 11px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
  padding: 0 4px;
}
</style>
